/**
 * **BGM 的"播放故障"滤镜 + 混响。**
 *
 * 评审：
 *
 * > 如果有可能的话，加一个 BGM 播放故障的音频滤镜，以及混响之类的效果，
 * > 自 IMPACT 时刻出现且达到最大，随后衰减消失。
 *
 * ## 这一串是什么
 *
 * ```
 *   master ──▶ 降位深(BitCrusher) ──▶ 低通往下扫(Filter) ──▶ 混响(Reverb) ──▶ 输出
 * ```
 *
 * - **降位深**：16 bit → 4 bit。数码故障最标志性的那一声（"沙沙的量化噪声"）。
 * - **低通往下扫**：16 kHz → 1.1 kHz。⚠️ 这里**原来挂的是失真**，评审说
 *   "有些刺耳"——削顶失真会在中高频叠出刺耳的和声，而"低通往下扫"是磁带/信号
 *   坏掉的听感（闷、掉高频），柔和得多。
 * - **混响**：衰减 2.5 秒的尾巴 —— "这一下之后空间变大了"。
 *
 * 三样的剂量都由**一个数**（`amount` 0..1）驱动：**打击那一刻给它 1**，
 * 然后按 `render/impact.ts` 的包络衰减回 0。0 的时候这条链是**透明的**
 * （16 bit、0 失真、干声），所以"平时"和没有它时听不出区别。
 *
 * ## ⚠️ 它是**懒建**的
 *
 * 两条理由：
 * 1. 没人抽到异常牌的话，这一串节点一个都不该存在（也就一分钱不花）；
 * 2. `Reverb` 构造时要生成脉冲响应，在 node（单测）里没有可用的
 *    `OfflineAudioContext` —— 懒建 + try/catch 让"没有音频环境"这条路
 *    和 `sfx.ts` 一样安静地退化成"没有效果"。
 */

import { BitCrusher, Filter, Gain, Reverb } from 'tone';

export interface BgmFxStyle {
  /** `amount = 0` 时的位深（16 = 基本听不出量化） */
  cleanBits: number;
  /** `amount = 1` 时的位深（越小越脏） */
  dirtyBits: number;
  /**
   * `amount = 0` 时的低通截止（Hz）。**听得见的全频**
   *
   * ⚠️ 这里原来挂的是 `Distortion`（削顶失真）—— 评审说"失真故障有些刺耳"。
   *    换成**低通往下扫**：那才是"磁带/信号坏掉"的听感（闷、糊、掉高频），
   *    而且它不会在耳朵最敏感的中高频上叠出刺耳的和声。
   */
  cleanTone: number;
  /** `amount = 1` 时的低通截止（Hz）。越低越"闷在被子里" */
  muffledTone: number;
  /** `amount = 1` 时的混响湿度（0..1） */
  maxReverb: number;
  /** 混响衰减（秒） */
  reverbDecay: number;
  /** 参数多快滑到目标（秒）。太快会"啪"一声，太慢就不像打击了 */
  rampSeconds: number;
}

export const DEFAULT_BGM_FX: BgmFxStyle = {
  cleanBits: 16,
  // 4 → 7 bit：量化噪声轻一档（4 bit 那层"沙"是刺耳的主要来源之一）
  dirtyBits: 7,
  cleanTone: 16000,
  muffledTone: 1100,
  maxReverb: 0.35,
  reverbDecay: 1.8,
  rampSeconds: 0.035,
};

export interface BgmFx {
  /** 上游（`BgmDirector` 的 master）接到这里 */
  readonly input: Gain;
  /** 0..1。打击那一刻给 1，然后按包络衰减 */
  setAmount(amount: number): void;
  /** 现在生效多少（读数 / 冒烟用） */
  readonly amount: number;
  dispose(): void;
}

/** 造不出来（没有音频环境）时返回 `null` —— 调用方照旧跑，只是没有效果 */
export function createBgmFx(style: Partial<BgmFxStyle> = {}): BgmFx | null {
  const s: BgmFxStyle = { ...DEFAULT_BGM_FX, ...style };
  try {
    const input = new Gain(1);
    const crusher = new BitCrusher({ bits: s.cleanBits });
    const damper = new Filter({ frequency: s.cleanTone, type: 'lowpass', rolloff: -24 });
    const reverb = new Reverb({ decay: s.reverbDecay, preDelay: 0.01 });
    reverb.wet.value = 0;
    input.chain(crusher, damper, reverb);
    reverb.toDestination();

    let amount = 0;
    return {
      input,
      setAmount(next) {
        const t = Number.isFinite(next) ? Math.min(Math.max(next, 0), 1) : 0;
        amount = t;
        // ⚠️ 用 `rampTo` 而不是直接赋值：位深一步跳过去会"啪"一声
        crusher.bits.rampTo(s.cleanBits - (s.cleanBits - s.dirtyBits) * t, s.rampSeconds);
        // 低通往下扫：16kHz → 1.1kHz（"信号坏掉"的那种闷，不刺耳）
        damper.frequency.rampTo(
          s.cleanTone - (s.cleanTone - s.muffledTone) * t,
          s.rampSeconds,
        );
        reverb.wet.rampTo(s.maxReverb * t, s.rampSeconds);
      },
      get amount() {
        return amount;
      },
      dispose() {
        for (const node of [input, crusher, damper, reverb]) {
          try {
            node.dispose();
          } catch {
            // 已经销毁过了
          }
        }
      },
    };
  } catch {
    return null;
  }
}
